import React from 'react';
import { FaBox, FaCalendarAlt, FaEye, FaChevronRight } from 'react-icons/fa';

const OrderListRenderer = ({ data }) => {
  const { orders, pagination } = data;

  const getStatusBadgeStyle = (statusColor) => {
    return {
      backgroundColor: statusColor + '20',
      color: statusColor,
      borderColor: statusColor + '40'
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FaBox className="text-blue-600" />
            <h3 className="font-semibold text-gray-900">Your Orders</h3>
          </div>
          {pagination && (
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} 
              ({pagination.totalItems} total)
            </span>
          )}
        </div>
      </div>

      {/* Orders list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {orders.map((order) => (
          <div key={order.orderId} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              {/* Order info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-semibold text-gray-900">{order.orderNumber}</h4>
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-medium border"
                    style={getStatusBadgeStyle(order.statusColor)}
                  >
                    {order.status}
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                  <div className="flex items-center space-x-1">
                    <FaCalendarAlt className="text-xs" />
                    <span>{formatDate(order.date)}</span>
                  </div>
                  <span>•</span>
                  <span>{order.shopName}</span>
                  <span>•</span>
                  <span>{order.itemCount} items</span>
                </div>

                {/* Preview items */}
                {order.previewItems && order.previewItems.length > 0 && (
                  <div className="flex items-center space-x-3 mb-3">
                    {order.previewItems.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-8 h-8 rounded object-cover"
                          />
                        )}
                        <span className="text-xs text-gray-600">
                          {item.name} {item.quantity > 1 && `(${item.quantity})`}
                        </span>
                      </div>
                    ))}
                    {order.previewItems.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{order.previewItems.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Order total and actions */}
              <div className="text-right ml-4">
                <div className="font-semibold text-gray-900 mb-2">
                  {order.currency} ${order.total ? order.total.toFixed(2) : '0.00'}
                </div>
                <button className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium">
                  <FaEye />
                  <span>View Details</span>
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination footer */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <button 
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Showing {Math.min(pagination.page * 10, pagination.totalItems)} of {pagination.totalItems} orders
            </span>
            <button 
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderListRenderer;
